import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Modal,
  IconButton,
  OutlinedInput,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import BuzzTable from '../../../components/BuzzTable/BuzzTable';
import EditIcon from '@material-ui/icons/Edit';
import KeyIcon from '@material-ui/icons/VpnKey';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import useToast from '../../../hooks/useToast';
import useLoader from '../../../hooks/useLoader';
import CustomForm from '../../../components/FormRenderer/CustomForm';
import {
  sanitizePayload,
  initializeFormData,
} from '../../../components/FormRenderer/utils';
import {
  createAkademyUser,
  updateAkademyUser,
  getAllAkademyUsers,
  bulkUploadAkademyUsers,
  addMasterCoordinatorProfile,
} from '../AkademySlice';
import moment from 'moment';
import UserUploadModal from './UserUploadModal';
import { Search } from '@material-ui/icons';
import { monthOptions, yearOptions } from '../../../utils/constants';

const AkademyUsers = () => {
  const dispatch = useDispatch();
  const { ErrorToast, SuccessToast } = useToast();
  const { handleLoader } = useLoader();
  const buzzTableRef = useRef();

  const [showUserUploadModal, setShowUserUploadModal] = useState(false);
  const [showFormModal, setFormModal] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [formTemplate, setFormTemplate] = useState(null);
  const [formData, setFormData] = useState({});

  const [search, setSearch] = useState('');

  const { users, schools, languages, classes, programmes, loading } =
    useSelector(({ akademyReducer }) => {
      return {
        users: akademyReducer.users,
        schools: akademyReducer.schools,
        languages: akademyReducer.languages,
        classes: akademyReducer.classes,
        programmes: akademyReducer.programmes,
        loading: akademyReducer.loading,
      };
    });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(getAllAkademyUsers({ search }))
        .unwrap()
        .then(() => {
          resetTablePageIdx();
        });
    }, 1500);
    return () => clearTimeout(timeoutId);
  }, [search]);

  const resetTablePageIdx = () => {
    if (buzzTableRef.current) buzzTableRef.current.resetPageIdx();
  };

  useEffect(() => {
    if (selectedRowId) {
      let newFormData = JSON.parse(
        JSON.stringify(users.filter((item) => item._id === selectedRowId)[0])
      );
      if (newFormData.address) {
        if (newFormData.address.city)
          newFormData.city = newFormData.address.city;
        if (newFormData.address.state)
          newFormData.state = newFormData.address.state;
        if (newFormData.address.country)
          newFormData.country = newFormData.address.country;
        if (newFormData.address.pincode)
          newFormData.pincode = newFormData.address.pincode;
        delete newFormData.address;
      }
      if (newFormData.subscriptionStartDate)
        newFormData.subscriptionStartDate =
          newFormData.subscriptionStartDate.split('T')[0];
      if (newFormData.subscriptionEndDate)
        newFormData.subscriptionEndDate =
          newFormData.subscriptionEndDate.split('T')[0];

      if (newFormData.class) {
        const classesStringArr = classes.map(({ code }) => {
          return code;
        });
        newFormData.class = newFormData.class.filter((cls) => {
          if (classesStringArr.includes(cls)) return true;
          return false;
        });
      }
      if (newFormData.language) {
        const languagesStringArr = languages.map(({ code }) => {
          return code;
        });
        newFormData.language = newFormData.language.filter((cls) => {
          if (languagesStringArr.includes(cls)) return true;
          return false;
        });
      }

      console.log(newFormData);
      setFormData(newFormData);
    } else setFormData(initializeFormData(formTemplate));
  }, [selectedRowId]);

  useEffect(() => {
    if (!formTemplate) return;

    if (formData.isAssociatedWithSchool === true) {
      let newFormTemplate = [...formTemplate];
      newFormTemplate.splice(5, 0, {
        id: 'form.school',
        title: 'School',
        slug: 'schoolId',
        type: 'Select',
        options: schools.map((school) => {
          return { label: school.name, value: school._id };
        }),
      });
      setFormTemplate(newFormTemplate);
    } else {
      setFormTemplate((template) => {
        template = template.filter((field) => field.slug !== 'schoolId');
        return template;
      });
      setFormData((state) => {
        state.schoolId = null;
        // state.class = [];
        // state.language = [];
        return state;
      });
    }
  }, [formData.isAssociatedWithSchool]);

  useEffect(() => {
    if (!formData || !formTemplate) return;

    if (formData.schoolId) {
      const selectedSchool = schools.filter(
        (school) => school._id === formData.schoolId
      )[0];

      let newFormTemplate = [...formTemplate];
      newFormTemplate = newFormTemplate.map((formField) => {
        if (formField.slug === 'class') {
          formField.options = classes
            .filter((item) => {
              if (selectedSchool['class'])
                return selectedSchool['class'].includes(item.code);
              else return true;
            })
            .map((option) => {
              return { label: option.name, value: option.code };
            });
        }
        if (formField.slug === 'language') {
          formField.options = languages
            .filter((item) => {
              if (selectedSchool['language'])
                return selectedSchool['language'].includes(item.code);
              else return true;
            })
            .map((option) => {
              return { label: option.name, value: option.code };
            });
        }
        return formField;
      });
      setFormTemplate(newFormTemplate);
    } else {
      let newFormTemplate = [...formTemplate];
      newFormTemplate = newFormTemplate.map((formField) => {
        if (formField.slug === 'class') {
          formField.options = classes.map((option) => {
            return { label: option.name, value: option.code };
          });
        }
        if (formField.slug === 'language') {
          formField.options = languages.map((option) => {
            return { label: option.name, value: option.code };
          });
        }
        return formField;
      });
      setFormTemplate(newFormTemplate);
    }
  }, [formData.schoolId]);

  useEffect(() => {
    if (!schools || !languages || !classes || !programmes) return;

    const formTemplate = [
      {
        id: 'form.name',
        title: 'Name',
        slug: 'name',
        type: 'String',
        required: true,
      },
      {
        id: 'form.mobile',
        title: 'Mobile',
        slug: 'mobile',
        type: 'String',
        required: true,
      },
      {
        id: 'form.emailId',
        title: 'Email Id',
        slug: 'email',
        type: 'String',
        required: true,
      },
      {
        id: 'form.password',
        title: 'Password',
        slug: 'password',
        type: 'Password',
      },
      //////////////////////////////////////////////////////////////////
      // {
      //   id: 'form.role',
      //   title: 'Role',
      //   slug: 'role',
      //   type: 'Select',
      //   options: [
      //     { label: 'Master Coordinator', value: 'MASTER_CO_ORDINATOR' },
      //     { label: 'School Admin', value: 'SCHOOL_ADMIN' },
      //     { label: 'School Coordinator', value: 'SCHOOL_CO_ORDINATOR' },
      //     { label: 'Educator', value: 'EDUCATOR_ADMIN' },
      //     { label: 'Teacher', value: 'SCHOOL_TEACHER' },
      //     { label: 'Parent - Personal', value: 'PERSONAL_PARENT' },
      //     { label: 'Parent - School', value: 'SCHOOL_PARENT' },
      //   ],
      //   required: true,
      // },
      // {
      //   id: 'form.isAssociatedWithSchool',
      //   title: 'Is User associated with a school?',
      //   slug: 'isAssociatedWithSchool',
      //   type: 'Boolean',
      // },
      // // {
      // //   id: 'form.school',
      // //   title: 'School',
      // //   slug: 'schoolId',
      // //   type: 'Select',
      // //   options: [
      // //     { label: 'Individual', value: null },
      // //     ...schools.map((school) => {
      // //       return { label: school.name, value: school._id };
      // //     }),
      // //   ],
      // // },
      // {
      //   id: 'form.language',
      //   title: 'Languages',
      //   slug: 'language',
      //   type: 'MultiSelect',
      //   options: languages.map((option) => {
      //     return { label: option.name, value: option.code };
      //   }),
      // },
      // {
      //   id: 'form.classes',
      //   title: 'Classes',
      //   slug: 'class',
      //   type: 'MultiSelect',
      //   options: classes.map((option) => {
      //     return { label: option.name, value: option.code };
      //   }),
      // },
      // {
      //   id: 'form.optedProgrammes',
      //   title: 'Opted Programmes',
      //   slug: 'optedProgrammes',
      //   type: 'Array',
      //   arrayElementType: [
      //     {
      //       id: 'code',
      //       title: 'Plan',
      //       slug: 'code',
      //       type: 'Select',
      //       options: programmes.map((option) => {
      //         return { label: option.name, value: option.code };
      //       }),
      //     },
      //     {
      //       id: 'startMonth',
      //       title: 'Start Month',
      //       slug: 'startMonth',
      //       type: 'Select',
      //       options: monthOptions,
      //     },
      //     {
      //       id: 'year',
      //       title: 'Start Year',
      //       slug: 'year',
      //       type: 'Select',
      //       options: yearOptions,
      //     },
      //   ],
      // },
      // {
      //   id: 'form.subscriptionStartDate',
      //   title: 'Subscription Start Date',
      //   slug: 'subscriptionStartDate',
      //   type: 'Date',
      // },
      // {
      //   id: 'form.subscriptionEndDate',
      //   title: 'Subscription End Date',
      //   slug: 'subscriptionEndDate',
      //   type: 'Date',
      // },
      //////////////////////////////////////////////////////////////////
      {
        id: 'form.address.city',
        title: 'City',
        slug: 'city',
        type: 'String',
      },
      {
        id: 'form.address.state',
        title: 'State',
        slug: 'state',
        type: 'String',
      },
      {
        id: 'form.address.country',
        title: 'Country',
        slug: 'country',
        type: 'String',
      },
      {
        id: 'form.address.pincode',
        title: 'Pincode',
        slug: 'pincode',
        type: 'String',
      },
    ];
    setFormTemplate(formTemplate);

    setFormData(initializeFormData(formTemplate));
  }, [schools, languages, programmes]);

  const usersColumn = [
    { Header: 'User Id', accessor: '_id' },
    // { Header: 'Role', accessor: 'role' },
    { Header: 'Name', accessor: 'name' },
    // { Header: 'Email', accessor: 'email' },
    // { Header: 'Mobile', accessor: 'mobile' },
    {
      Header: 'PII',
      Cell: (val) => {
        return (
          <>
            <p>{val.cell.row.original.email}</p>
            <p>{val.cell.row.original.mobile}</p>
          </>
        );
      },
    },
    {
      Header: 'Profiles',
      Cell: (val) => {
        const { profiles } = val.cell.row.original;
        return (
          <>
            {profiles &&
              profiles.length > 0 &&
              profiles
                .filter((profile) => profile._id)
                .map((profile, idx) => {
                  return (
                    <>
                      <p>Profile Id: {profile._id}</p>
                      <p>
                        Role: <span color="red">{profile.role}</span>
                      </p>
                      {profile.school && <p>School: {profile.school.name}</p>}
                      {idx !== profiles.length - 1 && <hr />}
                    </>
                  );
                })}
          </>
        );
      },
    },
    //////////////////////////////////////////////////////////////////////////
    // {
    //   Header: 'Opted Programmes',
    //   Cell: (val) => {
    //     if (val.cell.row.original.optedProgrammes)
    //       return val.cell.row.original.optedProgrammes.map((prog) => {
    //         return (
    //           <p>
    //             {prog.code} - {prog.startMonth} - {prog.year}
    //           </p>
    //         );
    //       });
    //     return null;
    //   },
    // },
    // { Header: 'School', accessor: 'schoolId' },
    // {
    //   Header: 'Classes',
    //   Cell: (val) => {
    //     const cl = val.cell.row.original.class;
    //     return <>{cl && cl.join(', ')}</>;
    //   },
    // },
    // {
    //   Header: 'Languages',
    //   Cell: (val) => {
    //     const { language } = val.cell.row.original;
    //     return <>{language && language.join(', ')}</>;
    //   },
    // },
    // {
    //   Header: 'Subscription',
    //   Cell: (val) => {
    //     const { subscriptionStartDate, subscriptionEndDate } =
    //       val.cell.row.original;

    //     const startDate = moment(subscriptionStartDate).format('DD MMM YYYY');
    //     const endDate = moment(subscriptionEndDate).format('DD MMM YYYY');

    //     return (
    //       <>
    //         <p>{subscriptionStartDate && startDate}</p>
    //         <p>{subscriptionEndDate && endDate}</p>
    //       </>
    //     );
    //   },
    // },
    //////////////////////////////////////////////////////////////////////////

    {
      Header: 'Edit',
      Cell: (val) => {
        const { _id, email, profiles } = val.cell.row.original;

        return (
          <>
            {(profiles.length == 0 ||
              (profiles.length > 0 &&
                !profiles
                  .map(({ role }) => role)
                  .includes('MASTER_CO_ORDINATOR'))) && (
              <IconButton
                onClick={() => handleMakeMasterCoordinator(_id, email)}>
                <KeyIcon color="secondary" />
              </IconButton>
            )}

            <IconButton onClick={() => handleEdit(_id)}>
              <EditIcon color="secondary" />
            </IconButton>
          </>
        );
      },
    },
  ];

  const openFormModal = () => setFormModal(true);
  const closeFormModal = () => setFormModal(false);

  const openUserUploadModal = () => setShowUserUploadModal(true);
  const closeUserUploadModal = () => setShowUserUploadModal(false);

  const handleEdit = (id) => {
    setSelectedRowId(id);
    openFormModal();
  };

  const handleCreateNew = () => {
    setSelectedRowId(null);
    openFormModal();
  };

  const forceRenderForm = () => {
    setFormTemplate((state) => {
      return JSON.parse(JSON.stringify(state));
    });
  };

  const handleSave = async () => {
    handleLoader(true);
    let payload = sanitizePayload(formData);

    dispatch(
      selectedRowId === null
        ? createAkademyUser(payload)
        : updateAkademyUser({ id: selectedRowId, data: payload })
    )
      .unwrap()
      .then((res) => {
        SuccessToast(res.message);
        dispatch(getAllAkademyUsers());
        closeFormModal();
      })
      .catch((error) => {
        ErrorToast(error.message);
      });

    handleLoader(false);
  };

  const handleBulkUploadUsers = (file) => {
    handleLoader(true);
    dispatch(bulkUploadAkademyUsers({ file }))
      .unwrap()
      .then((res) => {
        SuccessToast(res.message);
        dispatch(getAllAkademyUsers());
        closeUserUploadModal();
      })
      .catch((error) => {
        ErrorToast(error.message);
      });

    handleLoader(false);
  };

  const handleMakeMasterCoordinator = (id, email) => {
    if (
      !window.confirm(
        `Are you sure want to make ${email} a MASTER Coordinator?`
      )
    )
      return;

    handleLoader(true);
    dispatch(addMasterCoordinatorProfile({ id }))
      .unwrap()
      .then((res) => {
        SuccessToast(res.message);
        dispatch(getAllAkademyUsers());
        closeUserUploadModal();
      })
      .catch((error) => {
        ErrorToast(error.message);
      });

    handleLoader(false);
  };

  return (
    <Box style={{ width: '100%' }}>
      <Box
        display="flex"
        openUserUploadModal
        justifyContent={'space-between'}
        style={{ gap: '1rem' }}>
        <Button onClick={handleCreateNew} variant={'contained'} color="primary">
          Add User
        </Button>
        <FormControl sx={{ m: 1, width: '25ch' }} variant="outlined">
          <InputLabel htmlFor="search-akademy-users">Search</InputLabel>
          <OutlinedInput
            id="search-akademy-users"
            type={'text'}
            disabled={loading}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            endAdornment={
              <InputAdornment position="end">
                {!loading ? (
                  <Search />
                ) : (
                  <Box sx={{ display: 'flex' }}>
                    <CircularProgress size="1.5rem" />
                  </Box>
                )}
              </InputAdornment>
            }
            label="Search"
          />
        </FormControl>

        <Button
          onClick={openUserUploadModal}
          variant={'contained'}
          color="primary">
          Bulk Upload Users
        </Button>
      </Box>

      <Box
        my={2}
        style={{
          // backgroundColor: 'red',
          width: '100%',
          // overflow: 'scroll',
        }}>
        <BuzzTable
          ref={buzzTableRef}
          columns={usersColumn}
          data={users}
          isHandlePagination={true}
        />
      </Box>
      <Modal open={showFormModal} onClose={closeFormModal}>
        <CustomForm
          formFor={'User'}
          formTemplate={formTemplate}
          formData={formData}
          setFormData={(slug, newValue) => {
            setFormData((state) => {
              state[slug] = newValue;
              return state;
            });
            forceRenderForm();
          }}
          mode={selectedRowId === null ? 'CREATE' : 'EDIT'}
          handleSave={handleSave}
        />
      </Modal>
      <UserUploadModal
        show={showUserUploadModal}
        handleCloseModal={closeUserUploadModal}
        handleUpload={handleBulkUploadUsers}
      />
    </Box>
  );
};

export default AkademyUsers;
